import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Megaphone,
  TrendingUp,
  Eye,
  DollarSign,
  Plus,
  Calendar,
  Target,
  BarChart3,
  Mail,
  MessageSquare,
  Share2
} from 'lucide-react';

const EnhancedMarketing = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    type: 'Email',
    budget: '',
    startDate: '',
    endDate: '',
    description: ''
  });

  const handleCreateCampaign = () => {
    if (!campaignForm.name || !campaignForm.budget || !campaignForm.startDate) {
      alert('Please fill in required fields: Name, Budget, Start Date');
      return;
    }

    console.log('Creating campaign:', campaignForm);
    alert('Campaign created successfully!');
    setCampaignForm({
      name: '',
      type: 'Email',
      budget: '',
      startDate: '',
      endDate: '',
      description: ''
    });
    setShowCampaignModal(false);
  };

  const campaigns = [
    {
      id: 1,
      name: 'November Referral Drive',
      type: 'Email + Social',
      status: 'active',
      startDate: '2025-11-01',
      endDate: '2025-11-30',
      budget: '$2,500',
      spent: '$1,850',
      impressions: 45200,
      clicks: 3850,
      conversions: 127,
      roi: '340%'
    },
    {
      id: 2,
      name: 'Holiday Special Promotion',
      type: 'Social Media',
      status: 'scheduled',
      startDate: '2025-12-01',
      endDate: '2025-12-31',
      budget: '$3,000',
      spent: '$0',
      impressions: 0,
      clicks: 0,
      conversions: 0,
      roi: 'N/A'
    }
  ];

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 border-green-200',
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return <Badge className={colors[status]}>{status}</Badge>;
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
          <Megaphone className="h-8 w-8 mr-3 text-[#D4AF37]" />
          Marketing Center
        </h1>
        <p className="text-gray-600">Manage campaigns and track marketing performance</p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Campaigns</p>
                <p className="text-2xl font-bold text-[#0A1628]">40</p>
              </div>
              <Target className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Impressions</p>
                <p className="text-2xl font-bold text-[#0A1628]">124K</p>
              </div>
              <Eye className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conversion Rate</p>
                <p className="text-2xl font-bold text-[#0A1628]">34%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg ROI</p>
                <p className="text-2xl font-bold text-[#0A1628]">312%</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-[#0A1628]">Active Campaigns</h2>
        <Button 
          className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
          onClick={() => setShowCampaignModal(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </Button>
      </div>

      <div className="space-y-4">
        {campaigns.map((campaign) => (
          <Card key={campaign.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="font-semibold text-lg text-[#0A1628]">{campaign.name}</h3>
                    {getStatusBadge(campaign.status)}
                    <Badge variant="outline">{campaign.type}</Badge>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {campaign.startDate} - {campaign.endDate}
                    </span>
                    <span>
                      Budget: {campaign.budget} (Spent: {campaign.spent})
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-5 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Impressions</p>
                  <p className="text-xl font-bold text-[#0A1628]">{campaign.impressions.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">Clicks</p>
                  <p className="text-xl font-bold text-[#D4AF37]">{campaign.clicks.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">CTR</p>
                  <p className="text-xl font-bold text-blue-600">
                    {campaign.impressions > 0 ? ((campaign.clicks/campaign.impressions)*100).toFixed(2) : 0}%
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">Conversions</p>
                  <p className="text-xl font-bold text-green-600">{campaign.conversions}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">ROI</p>
                  <p className="text-xl font-bold text-[#D4AF37]">{campaign.roi}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Create New Campaign</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Campaign Name *</label>
                <Input 
                  placeholder="Enter campaign name"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select 
                  className="w-full px-3 py-2 border rounded-md"
                  value={campaignForm.type}
                  onChange={(e) => setCampaignForm({...campaignForm, type: e.target.value})}
                >
                  <option>Email</option>
                  <option>Social Media</option>
                  <option>Email + Social</option>
                  <option>Direct Mail</option>
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Budget *</label>
                  <Input 
                    placeholder="$0.00"
                    value={campaignForm.budget}
                    onChange={(e) => setCampaignForm({...campaignForm, budget: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date *</label>
                  <Input 
                    type="date"
                    value={campaignForm.startDate}
                    onChange={(e) => setCampaignForm({...campaignForm, startDate: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">End Date</label>
                <Input 
                  type="date"
                  value={campaignForm.endDate}
                  onChange={(e) => setCampaignForm({...campaignForm, endDate: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea 
                  placeholder="Campaign description and goals..."
                  rows={4}
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({...campaignForm, description: e.target.value})}
                />
              </div>
              <div className="flex space-x-2">
                <Button 
                  className="flex-1 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                  onClick={handleCreateCampaign}
                >
                  Create Campaign
                </Button>
                <Button 
                  onClick={() => setShowCampaignModal(false)} 
                  variant="outline" 
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedMarketing;
